<template lang='pug'>
  div(v-if='gitMode')
    v-badge(color='accent'
      @click.prevent.stop='openDialog'
      bottom
      left
      offset-x='18'
      offset-y='20'
      dot
      :value='uncommitted')
      v-tooltip(bottom)
        template(v-slot:activator='{on}')
          v-btn(v-on='on' @click='openDialog' text icon)
            font-awesome-icon(icon='code-branch' size='lg')
        span Open Git Dialog
    v-dialog(v-model='open')
      v-card(v-if='!loaded').loading
        bionic-metrics-page-loading
      v-card(v-if='loaded')
        v-card-title
          v-row(no-gutters)
            v-col
              .text-h5.font-weight-light Git Commit
              .text-caption.font-weight-light Branch: {{branch}}
              .text-caption.font-weight-light
                a(@click='openEditGitFilename=true') {{type}}/{{filename}}
            v-spacer
            v-row(no-gutters justify='end' align='center')
              bionic-metrics-saving.text-subtitle-1(:saving-status='savingStatus')
              v-btn(icon @click='open = false')
                v-icon close
        bionic-metrics-git-history(v-if='open' :type='type' :git-name='gitName')
        bionic-metrics-monaco-diff-editor.diff-editor(
          v-if='open && loaded'
          v-model='code'
          :original='original'
          :type='type'
          :guid='guid'
          :gitName='gitName')
        v-spacer
        v-card-actions
          v-text-field(v-if='uncommitted' :disabled='committing' type='text' label='Commit Message' hint='I changed xyz because of abc' v-model='message' required autofocus @keyup.enter="commit").mr-4
          v-spacer(v-if='!uncommitted')
          v-btn(text type='submit' color='primary' :disabled='isSaving || !uncommitted || !message' :loading='committing' @click.stop.prevent='commit') {{commitButtonText}}
    v-dialog(v-model='openEditGitFilename' max-width='400px')
      v-card
        form(@submit.prevent='changeGitFilename')
          v-card-title
            .text-subtitle-1 Change Git Filename
          v-card-text
            .text-body-2.mb-1
              | Changing the filename impacts <strong>all</strong> branches.&nbsp;
              | Before changing a filename, make sure this file is not in use in any other branch.
              | If this file is used in any other branch via a different
              | filename, the GitHub API will not be able to find it via a different filename.
            .body.mb-4
              | Changing the filename will remove history for this file.
            v-text-field(type='text' label='Git Filename' hint='someExample.js' v-model='gitFilename' required)
          v-card-actions
            v-spacer
            v-btn(text @click.native='openEditGitFilename=false') Cancel
            v-btn(text type='submit' color='primary' :disabled='gitName === gitFilename' :loading='changingFilename') Change Filename
</template>
<script>
import {get} from 'lodash';
import {mapGetters} from 'vuex';
import BionicMetricsGitHistory from './GitHistory';
import BionicMetricsMonacoDiffEditor from './MonacoDiffEditor';
import BionicMetricsSaving from './Saving';
import GitHub from '../api/github';
import Notifications from '../services/notification.service';
import BionicMetricsPageLoading from './PageLoading';
import FilenameMixin from '../mixins/filename.mixin';

export default {
  name: 'BionicMetricsGitCommit',
  components: {BionicMetricsPageLoading, BionicMetricsSaving, BionicMetricsMonacoDiffEditor, BionicMetricsGitHistory},
  mixins: [FilenameMixin],
  props: {
    type: {
      type: String,
      validator: (val) => ['test', 'module'].includes(val)
    },
    gitName: {
      type: String
    },
    guid: {
      type: String,
      required: true
    },
    uncommitted: {
      type: Boolean,
      default: false
    },
    value: {
      default() {
        return '';
      }
    }
  },
  asyncComputed: {
    original() {
      if (!this.open || !this.type || !this.gitName) return null;
      return GitHub.contents(this.type, this.gitName);
    }
  },
  computed: {
    loaded() {
      return this.original !== null && this.code;
    },
    filename() {
      return this.gitName || '--edit--';
    },
    commitButtonText() {
      return this.uncommitted ? 'Commit' : 'Nothing To Commit';
    },
    isSaving() {
      return this.savingStatus === 'SAVING';
    },
    branch() {
      return get(this.user, 'gitBranch');
    },
    gitFilename: {
      get() {
        if (this.gitNameTouched) return this.localGitName;
        return this.gitName;
      },
      set(value) {
        this.gitNameTouched = true;
        this.localGitName = value;
      }
    },
    code: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    ...mapGetters({
      user: 'getUser',
      account: 'getAccount',
      savingStatus: 'getSavingStatus',
      gitMode: 'getGitMode'
    })
  },
  methods: {
    codeChange(change) {
      this.$emit('input', change);
    },
    async openDialog() {
      this.open = true;
    },
    async commit() {
      try {
        this.committing = true;
        await GitHub.commit(this.type, this.guid, this.message, this.code);
        this.message = null;
        this.open = false;
        this.$emit('commit');
        await Notifications.toast(`Changes successfully committed to <strong>${this.branch}</strong>`);
      } catch (error) {
        console.error(error);
      } finally {
        this.committing = false;
      }
    },
    async changeGitFilename() {
      if (!this.isValidFilename(this.localGitName)) return Notifications.alert(`<strong>${this.localGitName}</strong> is an invalid filename. Filenames cannot contain spaces or special characters.`);
      try {
        this.changingFilename = true;
        await GitHub.rename(this.type, this.guid, this.gitName, this.localGitName, this.code);
        Notifications.toast(`Successfully updated git filename to <strong>${this.localGitName}</strong>`);
        this.$emit('commit');
        this.openEditGitFilename = false;
        this.open = false;
      } finally {
        this.changingFilename = false;
      }
    }
  },
  data() {
    return {
      open: false,
      message: null,
      committing: false,
      openEditGitFilename: false,
      gitNameTouched: false,
      localGitName: null,
      changingFilename: false,
      render: 0
    };
  }
};
</script>

<style scoped lang='scss'>
  .loading {
    height: 70vh;
  }

  .diff-editor {
    min-height: 50vh;
    height: 50vh;
    width: 100%;
  }
</style>
